import { ViewMorePopover } from '@/components/ats/ViewMorePopover/ViewMorePopover';
import { jobsEmploymentsOptions } from '@/constants/job-constants';
import { secondsToHumanTime } from '@/pages/reports/non-ats-reports/ats/table';
import { KImage, KText } from '@/ui-library/components';
import { Flex, Link } from '@chakra-ui/react';

const workTypeValueMap = Object.fromEntries(
  jobsEmploymentsOptions.map((opt) => [opt.value, opt.label])
);

const workplaceValueMap = {
  office: 'On-Site',
  remote: 'Remote',
  hybrid: 'Hybrid',
};

const normalizeToArray = (value: unknown) =>
  Array.isArray(value) ? value : value ? [value] : [];

const renderPeopleInitial = (
  items: Array<{ name?: string; avatar?: string }>
) => (
  <Flex w="100%" gap="6px" alignItems="center">
    {items?.map((person, i) => (
      <Flex key={i} alignItems="center" gap="6px">
        <KImage
          name={person?.name}
          src={person?.avatar}
          backgroundStyles={{ minWidth: '20px', minHeight: '20px' }}
        />
        <KText
          textTransform="capitalize"
          isTruncated
          enableTooltip={true}
          maxW="120px"
          size="sm-regular"
        >
          {person?.name}
        </KText>
      </Flex>
    ))}
  </Flex>
);

const renderPeopleOthers = (
  list: Array<{ name?: string; avatar?: string }>
) => (
  <Flex flexDir="column" gap=".25em">
    {list?.map((person, i) => (
      <Flex key={i} alignItems="center" gap=".25em">
        <KImage
          name={person?.name}
          src={person?.avatar}
          backgroundStyles={{ minWidth: '20px', minHeight: '20px' }}
        />
        <KText
          textTransform="capitalize"
          isTruncated
          enableTooltip={true}
          maxW="100%"
          size="sm-regular"
        >
          {person?.name}
        </KText>
      </Flex>
    ))}
  </Flex>
);

const parseOfficeNames = (value: unknown): string[] => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item && typeof item === 'object' && 'ats_office' in item) {
          const office = (item as { ats_office?: { name?: string } })
            .ats_office;
          return office?.name ?? '';
        }
        if (item && typeof item === 'object' && 'name' in item) {
          return (item as { name?: string }).name ?? '';
        }
        return String(item);
      })
      .filter(Boolean);
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed)
        ? parsed
            .map((item: unknown) =>
              item && typeof item === 'object' && 'ats_office' in item
                ? ((item as { ats_office?: { name?: string } }).ats_office
                    ?.name ?? '')
                : String(item)
            )
            .filter(Boolean)
        : [];
    } catch {
      return value.trim() ? [value.trim()] : [];
    }
  }
  return [];
};

const renderOfficeInitial = (items: string[]) => (
  <Flex w="100%" gap="6px" alignItems="center">
    {items?.map((name, i) => (
      <KText
        key={i}
        maxW="130px"
        enableTooltip={true}
        isTruncated
        size="sm-regular"
      >
        {name}
      </KText>
    ))}
  </Flex>
);

const renderOfficeOthers = (list: string[]) => (
  <Flex flexDir="column" gap=".25em">
    {list?.map((name, i) => (
      <Flex key={i} alignItems="center" gap=".25em">
        <KText
          fontSize="sm"
          color="secondary-700"
          isTruncated
          enableTooltip={true}
        >
          {name}
        </KText>
      </Flex>
    ))}
  </Flex>
);

const OfficeNames = (row: { original?: { office_names?: unknown } }) => {
  const { office_names } = row?.original ?? {};
  const offices = parseOfficeNames(office_names);

  return (
    <Flex w="100%">
      {offices?.length > 0 ? (
        <ViewMorePopover
          list={offices}
          max={1}
          type="officeList"
          renderInitial={renderOfficeInitial}
          renderOthers={renderOfficeOthers}
          emptyMsg="-"
          trigger="hover"
        />
      ) : (
        <KText size="sm-regular">-</KText>
      )}
    </Flex>
  );
};

export const Jobname = (row: {
  original?: { ats_job?: { name?: string; id?: string } };
}) => {
  const { ats_job = {} } = row?.original ?? {};
  const { name, id } = ats_job ?? {};
  return (
    <Link href={`/jobs/${id}`} target="_blank" w="100%">
      <KText
        maxW="100%"
        enableTooltip={true}
        isTruncated
        size="sm-regular"
        _hover={{
          textDecoration: 'underline',
          cursor: 'pointer',
          color: 'primary-500',
        }}
      >
        {name || '-'}
      </KText>
    </Link>
  );
};

export const TimeToClose = (row: { original?: { time_to_close?: number } }) => {
  const duration = row?.original?.time_to_close ?? 0;
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {secondsToHumanTime(duration)}
    </KText>
  );
};

const Recruiters = (row: { original?: { recruiters?: unknown } }) => {
  const { recruiters } = row?.original ?? {};
  const list = normalizeToArray(recruiters);
  return (
    <>
      {list?.length > 0 ? (
        <Flex w="100%">
          <ViewMorePopover
            list={list}
            max={1}
            type="people"
            renderInitial={renderPeopleInitial}
            renderOthers={renderPeopleOthers}
            emptyMsg="-"
            trigger="hover"
          />
        </Flex>
      ) : (
        '-'
      )}
    </>
  );
};

const HiringManagers = (row: { original?: { hiring_managers?: unknown } }) => {
  const { hiring_managers } = row?.original ?? {};
  const list = normalizeToArray(hiring_managers);
  return (
    <>
      {list?.length > 0 ? (
        <Flex w="100%">
          <ViewMorePopover
            list={list}
            max={1}
            type="people"
            renderInitial={renderPeopleInitial}
            renderOthers={renderPeopleOthers}
            emptyMsg="-"
            trigger="hover"
          />
        </Flex>
      ) : (
        '-'
      )}
    </>
  );
};

const DepartmentName = (row: {
  original?: { ats_department?: { name?: string } };
}) => {
  const { ats_department = {} } = row?.original ?? {};
  const { name } = ats_department ?? {};
  return (
    <Flex w="100%">
      <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
        {name || '-'}
      </KText>
    </Flex>
  );
};

const Workplace = (row: { original?: { workplace?: string } }) => {
  const { workplace = '' } = row?.original ?? {};
  const label =
    workplaceValueMap[workplace as keyof typeof workplaceValueMap] ?? workplace;
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {label || '-'}
    </KText>
  );
};

const WorkType = (row: { original?: { work_type?: string } }) => {
  const { work_type = '' } = row?.original ?? {};
  const label =
    workTypeValueMap[work_type as keyof typeof workTypeValueMap] ?? work_type;
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {label || '-'}
    </KText>
  );
};

const parseRequisitions = (
  value: unknown
): Array<{ code?: string; role_name?: string }> => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const renderRequisitionInitial = (
  items: Array<{ code?: string; role_name?: string }>
) => (
  <Flex w="100%" gap="6px" alignItems="center">
    {items?.slice(0, 1).map((req, i) => (
      <KText
        key={i}
        maxW="130px"
        enableTooltip={true}
        isTruncated
        size="sm-regular"
      >
        {typeof req === 'object'
          ? [req?.code, req?.role_name].filter(Boolean).join(' - ')
          : String(req)}
      </KText>
    ))}
  </Flex>
);

const renderRequisitionOthers = (
  list: Array<{ code?: string; role_name?: string }>
) => (
  <Flex flexDir="column" gap=".25em">
    {list?.map((req, i) => (
      <KText
        key={i}
        fontSize="sm"
        color="secondary-700"
        isTruncated
        enableTooltip={true}
      >
        {typeof req === 'object'
          ? [req?.code, req?.role_name].filter(Boolean).join(' - ')
          : String(req)}
      </KText>
    ))}
  </Flex>
);

const AssociatedRequisitions = (row: {
  original?: { associated_requisitions?: unknown };
}) => {
  const { associated_requisitions } = row?.original ?? {};
  const list = parseRequisitions(associated_requisitions);
  return (
    <Flex w="100%">
      {list?.length > 0 ? (
        <ViewMorePopover
          list={list}
          max={1}
          type="officeList"
          renderInitial={renderRequisitionInitial}
          renderOthers={renderRequisitionOthers}
          emptyMsg="-"
          trigger="hover"
        />
      ) : (
        <KText size="sm-regular">-</KText>
      )}
    </Flex>
  );
};

const JobCreatedBy = (row: {
  original?: { job_created_by?: { name?: string; avatar?: string } | string };
}) => {
  const { job_created_by } = row?.original ?? {};
  const name =
    typeof job_created_by === 'object' && job_created_by?.name
      ? job_created_by.name
      : typeof job_created_by === 'string'
        ? job_created_by
        : '';
  const avatar =
    typeof job_created_by === 'object' ? job_created_by?.avatar : undefined;
  return name ? (
    <Flex w="100%" alignItems="center" gap="6px">
      <KImage
        name={name}
        src={avatar}
        backgroundStyles={{ minWidth: '20px', minHeight: '20px' }}
      />
      <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
        {name}
      </KText>
    </Flex>
  ) : (
    <KText size="sm-regular">-</KText>
  );
};

const JobClosedBy = (row: {
  original?: { job_closed_by?: { name?: string; avatar?: string } | string };
}) => {
  const { job_closed_by } = row?.original ?? {};
  const name =
    typeof job_closed_by === 'object' && job_closed_by?.name
      ? job_closed_by.name
      : typeof job_closed_by === 'string'
        ? job_closed_by
        : '';
  const avatar =
    typeof job_closed_by === 'object' ? job_closed_by?.avatar : undefined;
  return name ? (
    <Flex w="100%" alignItems="center" gap="6px">
      <KImage
        name={name}
        src={avatar}
        backgroundStyles={{ minWidth: '20px', minHeight: '20px' }}
      />
      <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
        {name}
      </KText>
    </Flex>
  ) : (
    <KText size="sm-regular">-</KText>
  );
};

export {
  AssociatedRequisitions,
  DepartmentName,
  HiringManagers,
  JobClosedBy,
  JobCreatedBy,
  OfficeNames,
  Recruiters,
  WorkType,
  Workplace,
};
